import { z } from "zod";

export type WeightUnit = "kg" | "lb";

export const setMeasurements = z.object({
  weight: z.number().finite().min(0).max(2000).nullable().optional(),
  reps: z.number().int().min(0).max(10000).nullable().optional(),
  duration: z.number().int().min(0).max(604800).nullable().optional(),
  distance: z.number().finite().min(0).max(1000000).nullable().optional(),
  restSeconds: z.number().int().min(0).max(86400).nullable().optional(),
  rpe: z.number().finite().min(1).max(10).nullable().optional(),
  setType: z
    .enum(["working", "warmup", "dropset", "failure", "rest_pause"])
    .optional(),
  isCompleted: z.boolean().optional(),
});

export function weightToKg(value: number, unit: "kg" | "lb") {
  return unit === "lb" ? value * 0.45359237 : value;
}
export function weightFromKg(value: number, unit: "kg" | "lb") {
  return unit === "lb" ? value / 0.45359237 : value;
}
export function setMeasurementText(set: {
  reps: number | null;
  duration?: number | null;
  distance?: number | null;
}) {
  return (
    [
      set.reps != null ? `${set.reps} reps` : null,
      set.duration != null ? `${set.duration} s` : null,
      set.distance != null ? `${set.distance} m` : null,
    ]
      .filter(Boolean)
      .join(" · ") || "—"
  );
}

/** Null provenance means an older value whose unit was never recorded. Never
 * infer it from the owner's current preference. */
export function knownWeightKg(
  value: number | null | undefined,
  unit: WeightUnit | null | undefined,
): number | null {
  return value == null || unit == null ? null : weightToKg(value, unit);
}
export function displayWeight(
  value: number | null | undefined,
  storedUnit: WeightUnit | null | undefined,
  displayUnit: WeightUnit,
): number | null {
  if (value == null) return null;
  if (storedUnit == null) return value;
  // Round only the view. Never round canonical storage on preference changes.
  return (
    Math.round(weightFromKg(weightToKg(value, storedUnit), displayUnit) * 100) /
    100
  );
}
export function projectWorkoutWeights<
  T extends {
    weightUnit?: WeightUnit | null;
    totalVolume: number | null;
    exercises: Array<{
      totalVolume: number | null;
      sets: Array<{ weight: number | null; volume: number | null }>;
    }>;
  },
>(workout: T, unit: WeightUnit): T {
  const convert = (value: number | null) =>
    displayWeight(value, workout.weightUnit, unit);
  return {
    ...workout,
    weightUnit: workout.weightUnit == null ? null : unit,
    totalVolume: convert(workout.totalVolume),
    exercises: workout.exercises.map((exercise) => ({
      ...exercise,
      totalVolume: convert(exercise.totalVolume),
      sets: exercise.sets.map((set) => ({
        ...set,
        weight: convert(set.weight),
        volume: convert(set.volume),
      })),
    })),
  };
}
